import cx from 'classnames';
import type { ChangeEvent, FunctionComponent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureGate } from '@trello/feature-gate-client';
import { intl } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
import { SearchIcon } from '@trello/nachos/icons/search';
import { Select } from '@trello/nachos/select';
import { Textfield } from '@trello/nachos/textfield';
import { token } from '@trello/theme';

import { CollectionsSelect } from 'app/src/components/CollectionsSelect';
import { useWorkspaceBoardsFilterState } from './useWorkspaceBoardsFilterState';
import type { SortKey } from './workspaceBoardsFilterState';

import styles from './WorkspaceBoardsFilter.less';

export interface Option {
  value: string;
  label: string;
}

const sortKeys: SortKey[] = [
  'most-recent',
  'least-recent',
  'alphabetical-a-z',
  'alphabetical-z-a',
];

const sortOptions = [
  {
    value: sortKeys[0],
    label: intl.formatMessage({
      id: `templates.workspace_page.${sortKeys[0]}`,
      defaultMessage: 'Most recently active',
      description: 'Most recently active',
    }),
  },
  {
    value: sortKeys[1],
    label: intl.formatMessage({
      id: `templates.workspace_page.${sortKeys[1]}`,
      defaultMessage: 'Least recently active',
      description: 'Least recently active',
    }),
  },
  {
    value: sortKeys[2],
    label: intl.formatMessage({
      id: `templates.workspace_page.${sortKeys[2]}`,
      defaultMessage: 'Alphabetical A-Z',
      description: 'Alphabetical A-Z',
    }),
  },
  {
    value: sortKeys[3],
    label: intl.formatMessage({
      id: `templates.workspace_page.${sortKeys[3]}`,
      defaultMessage: 'Alphabetical Z-A',
      description: 'Alphabetical Z-A',
    }),
  },
];

interface WorkspaceBoardsFilterProps {
  orgId: string;
}
export const WorkspaceBoardsFilter: FunctionComponent<
  WorkspaceBoardsFilterProps
> = ({ orgId }) => {
  const {
    searchQuery,
    sort,
    tagFilter,
    setSearchQuery,
    setSortOrder,
    setTagFilter,
  } = useWorkspaceBoardsFilterState();

  const { value: isM2WorkspaceNavEnabled } = useFeatureGate(
    'billplat_m2_workspace_pages',
  );

  const [internalSearchQuery, setInternalSearchQuery] = useState(searchQuery);
  const activeSort = sortOptions.find(
    (sortKey: Option) => sortKey.value === sort,
  );

  const onCollectionChanged = useCallback(
    async (tagId?: string) => {
      if (tagFilter === tagId) return;
      if (!tagId || tagId === tagFilter) {
        setTagFilter(null);
      } else {
        setTagFilter(tagId);
      }
    },
    [tagFilter, setTagFilter],
  );

  const onSortSelected = useCallback(
    async (option: Option | null) => {
      if (sort === option?.value) return;

      Analytics.sendUIEvent({
        action: 'clicked',
        actionSubject: 'dropdownItem',
        actionSubjectId: 'sortDropdownItem',
        source: 'workspaceBoardsScreen',
        containers: {
          workspace: {
            id: orgId,
          },
        },
        attributes: {
          sortOption: option?.value,
        },
      });
      setSortOrder(option?.value as SortKey);
    },
    [sort, orgId, setSortOrder],
  );

  const onSearchChangedDebounced = useDebouncedCallback(setSearchQuery, 150);

  useEffect(() => {
    onSearchChangedDebounced(internalSearchQuery);
  }, [internalSearchQuery, onSearchChangedDebounced]);

  const onSearchChanged = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      setInternalSearchQuery(e.target.value),
    [setInternalSearchQuery],
  );

  const onSearchClick = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'input',
      actionSubjectId: 'searchInput',
      source: 'workspaceBoardsScreen',
      containers: {
        workspace: {
          id: orgId,
        },
      },
    });
  }, [orgId]);

  const onClearSearch = useCallback(
    () => setInternalSearchQuery(''),
    [setInternalSearchQuery],
  );

  const onMenuOpen = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'dropdown',
      actionSubjectId: 'sortDropdown',
      source: 'workspaceBoardsScreen',
      containers: {
        workspace: {
          id: orgId,
        },
      },
    });
  }, [orgId]);

  return (
    <div className={styles.filterBar}>
      <div
        className={cx(styles.sortFilterElement, {
          [styles.fullscreen]: isM2WorkspaceNavEnabled,
        })}
      >
        <div className={styles.formElement}>
          <label htmlFor="sort-by" className={styles.label}>
            <FormattedMessage
              id="templates.workspace_page.sort-by"
              defaultMessage="Sort by"
              description="Sort by"
            />
          </label>
          <Select
            className={styles.sortDropdown}
            containerClassName={styles.selectContainer}
            id="sort-by"
            defaultValue={activeSort}
            options={sortOptions}
            onChange={onSortSelected}
            onMenuOpen={onMenuOpen}
          ></Select>
        </div>

        <div className={styles.formElement}>
          <CollectionsSelect
            orgId={orgId}
            onSelect={onCollectionChanged}
            collectionId={tagFilter}
          />
        </div>
      </div>

      <div className={styles.searchFormElement}>
        <label htmlFor="search" className={styles.label}>
          {
            <FormattedMessage
              id="templates.workspace_page.search"
              defaultMessage="Search"
              description="Search"
            />
          }
        </label>
        <Textfield
          className={styles.textfield}
          onClick={onSearchClick}
          iconBefore={
            <SearchIcon
              size="small"
              color={token('color.icon.subtle', '#626F86')}
            />
          }
          iconAfter={
            internalSearchQuery ? (
              <Button
                iconBefore={
                  <CloseIcon
                    size="small"
                    color={token('color.icon', '#44546F')}
                  />
                }
                appearance="subtle"
                className={styles.closeButton}
                onClick={onClearSearch}
                aria-label={intl.formatMessage({
                  id: 'templates.workspace_page.clear-boards-search',
                  defaultMessage: 'Clear boards search.',
                  description: 'Clear boards search',
                })}
              />
            ) : undefined
          }
          value={internalSearchQuery}
          id="search"
          placeholder={intl.formatMessage({
            id: 'templates.workspace_page.search-boards',
            defaultMessage: 'Search boards',
            description: 'Search boards',
          })}
          onChange={onSearchChanged}
        ></Textfield>
      </div>
    </div>
  );
};
